import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import GlobalLoader from "../../components/ui/GlobalLoader";
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  Calendar,
  DollarSign,
  Users,
  Activity,
  Star,
  Pill,
  Phone,
  User,
  Mail,
  MapPin,
} from "lucide-react";
import { useAppSelector } from "@/redux/hooks";
import axiosInstance from "@/api/axiosInstance";
import { getAnalyticsData } from "@/api/orderService";
import { useNavigate } from "react-router-dom";
import { 
  normalizeAnalyticsData,
  // debugDataConsistency,
  getTimeAgo 
} from "../../utils/dataUtils";
// import { quickCreateTestOrders } from "../../utils/testOrderGenerator";
// import { quickCreateSimpleTestOrders } from "../../utils/simpleTestOrders";
// import { quickCreateWorkingTestOrders } from "../../utils/workingTestOrders";
// import { quickCreateRobustTestOrders } from "../../utils/robustTestOrders";

interface Stats {
  totalOrders: number;
  todayOrders: number;
  deliveredOrders: number;
  inTransitOrders: number;
  pendingOrders: number;
  failedOrders: number;
  totalRevenue: number;
  pickupRequested: number;
}


export function PharmacyDashboardPage() {
  const user = useAppSelector((state) => state.auth.user);
  const [pharmacyOrders, setPharmacyOrders] = useState([]);
  const [pharmacyStat, setPharmacyStat] = useState<Stats>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard specific data states
  const [recentOrders, setRecentOrders] = useState([]);
  const [deliveryTrends, setDeliveryTrends] = useState([]);
  const [topMedications, setTopMedications] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [deliveryTypeBreakdown, setDeliveryTypeBreakdown] = useState(null);
  
  const navigate = useNavigate();
  // Fetch dashboard data from consolidated API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 PharmacyDashboardPage: Fetching analytics from unified service...');
      
      // Use unified order service for consistent data
      const { basicStats, recentOrders, deliveryInsights, medicationCategories } = await getAnalyticsData('7d');
      
      console.log('✅ PharmacyDashboardPage: Analytics fetched from unified service', {
        totalOrders: basicStats.totalOrders,
        todayOrders: basicStats.todayOrders,
        recentOrdersCount: recentOrders.length,
        insightsCount: deliveryInsights.length
      });
      
      // debugDataConsistency('Dashboard - Unified Service Analytics', { basicStats, recentOrders, deliveryInsights });
      
      // Set basic stats from unified service
      setPharmacyStat({
        totalOrders: basicStats.totalOrders,
        todayOrders: basicStats.todayOrders,
        deliveredOrders: basicStats.deliveredOrders,
        inTransitOrders: basicStats.inTransitOrders,
        pendingOrders: basicStats.pendingOrders,
        failedOrders: basicStats.failedOrders,
        totalRevenue: basicStats.totalRevenue,
        pickupRequested: basicStats.pickupRequested
      });
      
      // Set recent orders from unified service
      setRecentOrders(recentOrders);
      
      // Set analytics data from unified service
      setDeliveryTrends(deliveryInsights.map((item: any) => ({
        day: item.day,
        orders: item.deliveries || 0,
        completed: Math.round((item.deliveries || 0) * (item.successRate || 0) / 100),
        revenue: (item.deliveries || 0) * 50 // Estimate revenue
      })));
      
      setTopMedications(medicationCategories.map((cat: any) => ({
        name: cat.name,
        prescriptions: cat.count || 0,
        revenue: cat.revenue || 0
      })));
      
      // Clear hourly and delivery type data for now (will be calculated from orders)
      setHourlyData([]);
      setDeliveryTypeBreakdown(null);
      
    } catch (error: any) {
      console.error('❌ PharmacyDashboardPage: Error fetching analytics:', error);
      setError(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Helper functions to check if we have real API data
  const hasRecentOrders = recentOrders.length > 0;
  const hasDeliveryTrends = deliveryTrends.length > 0;
  const hasTopMedications = topMedications.length > 0;

  // Calculate best performing day from delivery trends
  const getBestPerformingDay = () => {
    if (!hasDeliveryTrends) return null;
    
    const bestDay = deliveryTrends.reduce((best, current) => {
      const currentRate = current.orders > 0 ? (current.completed / current.orders) * 100 : 0;
      const bestRate = best.orders > 0 ? (best.completed / best.orders) * 100 : 0;
      return currentRate > bestRate ? current : best;
    });
    
    return {
      day: bestDay.day,
      completionRate: bestDay.orders > 0 ? ((bestDay.completed / bestDay.orders) * 100).toFixed(1) : '0',
    };
  };

  const bestPerformingDay = getBestPerformingDay();

  // Calculate delivery performance from orderStatus data and delivery type breakdown
  const getDeliveryPerformance = () => {
    if (!pharmacyStat) return null;
    
    const total = (pharmacyStat.deliveredOrders || 0) + (pharmacyStat.failedOrders || 0) + (pharmacyStat.inTransitOrders || 0) + (pharmacyStat.pendingOrders || 0);
    if (total === 0) return null;
    
    const deliverySuccessRate = total > 0 ? ((pharmacyStat.deliveredOrders || 0) / total * 100).toFixed(1) : '0';
    
    // Use delivery type breakdown if available, otherwise fallback to overall rate
    let sameDaySuccess = deliverySuccessRate;
    let nextDaySuccess = deliverySuccessRate;
    let scheduledSuccess = deliverySuccessRate;
    
    if (deliveryTypeBreakdown) {
      sameDaySuccess = deliveryTypeBreakdown.sameDay?.successRate?.toFixed(1) || deliverySuccessRate;
      nextDaySuccess = deliveryTypeBreakdown.nextDay?.successRate?.toFixed(1) || deliverySuccessRate;
      scheduledSuccess = deliveryTypeBreakdown.scheduled?.successRate?.toFixed(1) || deliverySuccessRate;
    }
    
    return {
      onTimeDelivery: deliverySuccessRate,
      sameDaySuccess,
      nextDaySuccess,
      scheduledSuccess,
      totalDeliveries: pharmacyStat.deliveredOrders || 0,
      failedDeliveries: pharmacyStat.failedOrders || 0,
      hasBreakdown: !!deliveryTypeBreakdown,
    };
  };

  const deliveryPerformance = getDeliveryPerformance();

  // Calculate peak hours from hourly data
  const getPeakHours = () => {
    if (!hourlyData || hourlyData.length === 0) return null;
    
    // Find the hour with highest order volume
    const peakHour = hourlyData.reduce((max, current) => 
      current.orders > max.orders ? current : max
    );
    
    // Find top 3 hours for better insights
    const sortedHours = [...hourlyData]
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 3);
    
    return {
      peakHour: peakHour.hour,
      peakOrders: peakHour.orders,
      topHours: sortedHours.map(h => ({
        hour: h.hour,
        orders: h.orders,
        deliveries: h.deliveries
      }))
    };
  };

  const peakHours = getPeakHours();

  // Calculate dynamic stats from API data
  const calculateDynamicStats = () => {
    const totalOrders = (pharmacyStat?.totalOrders ?? 0) || recentOrders.length;
    const deliveredOrders = pharmacyStat?.deliveredOrders ?? 0;
    const successRate = totalOrders > 0 ? Math.round((deliveredOrders / totalOrders) * 100) : 0;
    const weeklyOrders = hasDeliveryTrends ? deliveryTrends.reduce((sum, day) => sum + day.orders, 0) : 0;
    const weeklyRevenue = hasDeliveryTrends ? deliveryTrends.reduce((sum, day) => sum + day.revenue, 0) : 0;
    
    return {
      totalOrders,
      todayOrders: pharmacyStat?.todayOrders ?? 0,
      weeklyOrders,
      monthlyOrders: Math.round(weeklyOrders * 4.3), // Estimate monthly from weekly
      pendingOrders: pharmacyStat?.pendingOrders ?? 0,
      pickupRequested: pharmacyStat?.pickupRequested ?? 0,
      inTransitOrders: pharmacyStat?.inTransitOrders ?? 0,
      deliveredOrders,
      failedOrders: pharmacyStat?.failedOrders ?? 0,
      // avgDeliveryTime: 38, // TODO: Get from analytics API
      deliverySuccessRate: successRate,
      // customerSatisfaction: 4.8, // TODO: Get from analytics API
      totalRevenue: pharmacyStat?.totalRevenue ?? 0,
      todayRevenue: Math.round((pharmacyStat?.totalRevenue ?? 0) / 30), // Estimate daily from total
      weeklyRevenue: Math.round(weeklyRevenue),
    };
  };

  const pharmacyStats = calculateDynamicStats();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "assigned":
        return "bg-orange-100 text-orange-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "pickup-requested":
        return "bg-blue-100 text-blue-800";
      case "in-transit":
        return "bg-purple-100 text-purple-800";
      case "out-for-delivery":
        return "bg-orange-100 text-orange-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'same-day':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'next-day':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "assigned":
        return <AlertCircle className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "pickup-requested":
        return <Package className="w-4 h-4" />;
      case "in-transit":
        return <Truck className="w-4 h-4" />;
      case "out-for-delivery":
        return <Truck className="w-4 h-4" />;
      case "delivered":
        return <CheckCircle className="w-4 h-4" />;
      case "failed":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  // Loading state
  if (loading) {
    return (
      <GlobalLoader 
        isLoading={true} 
        message="Loading dashboard data..." 
        subMessage="Please wait while we fetch your pharmacy dashboard" 
      />
    );
  }

  // Error state
  // if (error) {
  //   return (
  //     <div className="space-y-6">
  //       <div className="flex items-center justify-center h-64">
  //         <div className="text-center">
  //           <div className="text-red-500 mb-4">
  //             <Package className="w-12 h-12 mx-auto" />
  //           </div>
  //           <p className="text-red-600 font-medium mb-2">Error loading dashboard data</p>
  //           <p className="text-gray-600 mb-4">{error}</p>
  //           <Button onClick={fetchDashboardData} className="bg-blue-600 hover:bg-blue-700">
  //             Try Again
  //           </Button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          {/* <h1 className="text-3xl font-semibold text-gray-900">
            Pharmacy Dashboard
          </h1> */}
          <p className="text-gray-600 mt-1">
            Welcome back, {user?.firstName}! Here's your {user?.pharmacyName}{" "}
            overview.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button className="bg-green-600 text-white hover:bg-green-700" onClick={() => navigate("/pharmacy/pickup")}>
            <Package className="w-4 h-4 mr-2" />
            Request Pickup
          </Button>
          {/* <Button variant="outline" onClick={() => navigate("/pharmacy/analytics")}>
            <Activity className="w-4 h-4 mr-2" />
            View Analytics
          </Button> */}
          {/* <Button 
            variant="outline" 
            onClick={async () => {
              console.log('🧪 Creating robust test orders...');
              const result = await quickCreateRobustTestOrders();
              if (result.success) {
                console.log(`✅ Successfully created ${result.created} test orders`);
                // Refresh dashboard data after creating test orders
                setTimeout(() => {
                  fetchDashboardData();
                }, 2000);
              } else {
                console.error(`❌ Failed to create test orders: ${result.errors.join(', ')}`);
              }
            }}
            className="bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100"
          >
            <Activity className="w-4 h-4 mr-2" />
            Create Test Data
          </Button> */}
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">
                  Today's Orders
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {pharmacyStats.todayOrders}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  +{Math.round(((pharmacyStats.todayOrders - 18) / 18) * 100)}%
                  vs yesterday
                </p>
              </div>
              <Package className="w-8 h-8 text-blue-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">
                  Success Rate
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {pharmacyStats?.deliverySuccessRate?.toFixed(1) || '0'}%
                </p>
                {/* <p className="text-xs text-green-600 mt-1">
                  Avg {pharmacyStats.avgDeliveryTime}min delivery
                </p> TODO: Add back when avgDeliveryTime is available from API */}
                <p className="text-xs text-green-600 mt-1">
                  Success Rate Tracking
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">In Transit</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pharmacyStats.inTransitOrders}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Active deliveries
                </p>
              </div>
              <Truck className="w-8 h-8 text-purple-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card> */}

        {/* <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">
                  Customer Rating
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  N/A
                </p>
                <p className="text-xs text-orange-600 mt-1">Out of 5.0 stars</p>
              </div>
              <Star className="w-8 h-8 text-orange-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card> */}
      </div>

      {/* Revenue & Performance Summary */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-700 font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-green-800">
                  ${pharmacyStats.totalRevenue.toLocaleString()}
                </p>
                <p className="text-sm text-green-600">
                  Today: ${pharmacyStats.todayRevenue.toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-700 font-medium">Monthly Orders</p>
                <p className="text-2xl font-bold text-blue-800">
                  {pharmacyStats.monthlyOrders}
                </p>
                <p className="text-sm text-blue-600">
                  This week: {pharmacyStats.weeklyOrders}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-700 font-medium">Avg Order Value</p>
                <p className="text-2xl font-bold text-purple-800">
                  
                  { pharmacyStats?.totalRevenue > 0 && pharmacyStats?.totalOrders > 0 ?
                    `$ ` +
                    (
                      pharmacyStats.totalRevenue / pharmacyStats.totalOrders
                    ).toFixed(2)

                    : `$ 0`
                  }
                </p>
                <p className="text-sm text-purple-600">+8.5% vs last month</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Main Dashboard Content */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="orders"
            className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            Recent Orders
          </TabsTrigger>
          {/* <TabsTrigger
            value="analytics"
            className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            Analytics
          </TabsTrigger>
          <TabsTrigger
            value="performance"
            className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            Performance
          </TabsTrigger> */}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium">Pending Orders</span>
                    </div>
                    <span className="text-xl font-bold text-yellow-700">
                      {pharmacyStats.pendingOrders}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Pickup Requested</span>
                    </div>
                    <span className="text-xl font-bold text-blue-700">
                      {pharmacyStats.pickupRequested}
                    </span>
                  </div>
                  
                  {/* <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Truck className="w-5 h-5 text-purple-600" />
                      <span className="font-medium">In Transit</span>
                    </div>
                    <span className="text-xl font-bold text-purple-700">
                      {pharmacyStats.inTransitOrders}
                    </span>
                  </div> */}
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Delivered Today</span>
                    </div>
                    <span className="text-xl font-bold text-green-700">
                      {pharmacyStats.deliveredOrders}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <span className="font-medium">Failed Deliveries</span>
                    </div>
                    <span className="text-xl font-bold text-red-700">
                      {pharmacyStats.failedOrders}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Weekly Performance */}
            {hasDeliveryTrends && (
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Order Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 flex flex-col gap-4 justify-around p-6">
                    {deliveryTrends.map((day) => (
                      <div
                        key={day.day}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm font-large w-20">
                          {day.day}
                        </span>
                        <div className="flex-1 mx-4">
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-3">
                              <div
                                className="bg-green-500 h-3 rounded-full"
                                style={{
                                  width: `${
                                    day.orders > 0 ? (day.completed / day.orders) * 100 : 0
                                  }%`,
                                }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 min-w-0">
                              {day.completed}/{day.orders}
                            </span>
                          </div>
                        </div>
                        {day.orders > 0 && (
                          <span className="text-sm font-bold text-green-600 min-w-0">
                            {Math.round((day.completed / day.orders) * 100)}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {!hasDeliveryTrends && (
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Order Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">No delivery trends available</p>
                    <p className="text-sm text-gray-400">Trends will appear here once you have analytics data</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Recent Orders Tab */}
        <TabsContent value="orders">
          {hasRecentOrders ? (
            <Card>
              <CardHeader>
                <CardTitle>Recent Order Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                    {recentOrders.map((order: any) => (
                      <div key={order._id || order.id} className="border rounded-lg p-5 hover:shadow-md transition-shadow bg-white">
                        {/* Header Section */}
                        <div className="flex items-start justify-between mb-4 pb-4 border-b">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {order.orderNumber || `Order #${order._id?.slice(-6) || order.id?.slice(-6)}`}
                              </h3>
                              <Badge className={getStatusColor(order.pickupStatus || order.status)}>
                                {order.pickupStatus || order.status}
                              </Badge>
                              {order.priority && (
                                <Badge variant="outline" className={getPriorityColor(order.priority)}>
                                  {order.priority === 'same-day' ? 'Same Day' : 'Next Day'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Main Content Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                          {/* Customer Info */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-700">Customer</span>
                            </div>
                            <p className="font-medium text-gray-900">{order.customerName || 'N/A'}</p>
                            {order.customerPhone && (
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <Phone className="w-3 h-3" />
                                <span>{order.customerPhone}</span>
                              </div>
                            )}
                            {order.customerEmail && (
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <Mail className="w-3 h-3" />
                                <span className="truncate">{order.customerEmail}</span>
                              </div>
                            )}
                          </div>

                          {/* Delivery Info */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-gray-700">Delivery Location</span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2">
                              {typeof order.location === 'object' && order.location?.address
                                ? order.location.address
                                : typeof order.location === 'string'
                                ? order.location
                                : 'Location not specified'}
                            </p>
                            {order.deliveryDate && (
                              <div className="flex items-center gap-1 text-sm text-gray-600 mt-2">
                                <Calendar className="w-3 h-3" />
                                Delivery: 
                                <span>{new Date(order.deliveryDate).toLocaleDateString()}</span>
                                {order.deliveryTime && (
                                  <>
                                    <Clock className="w-3 h-3 ml-2" />
                                    <span>{order.deliveryTime}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Order Summary */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Package className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-medium text-gray-700">Order Summary</span>
                            </div>
                            <p className="text-sm text-gray-600">{order.medicines?.length || 0} medicine(s)</p>
                          </div>
                        </div>
                        
                        {/* Medicines Preview */}
                        {order.medicines && order.medicines.length > 0 && (
                          <div className="pt-4 border-t">
                            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Medicines</p>
                            <div className="flex flex-wrap gap-2">
                              {order.medicines.slice(0, 4).map((medicine: any, index: number) => (
                                <Badge key={index} variant="secondary" className="text-xs">
                                  {medicine.name} {medicine.quantity ? `(×${medicine.quantity})` : ''}
                                </Badge>
                              ))}
                              {order.medicines.length > 4 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{order.medicines.length - 4} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Special Notes */}
                        {order.notes && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Special Instructions</p>
                            <p className="text-sm text-gray-700 line-clamp-2">{order.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Recent Order Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No recent orders available</p>
                  <p className="text-sm text-gray-400">Orders will appear here once you have data from the API</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {hasTopMedications && (
              <Card>
                <CardHeader>
                  <CardTitle>Top Medications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {topMedications.map((med, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{med.name}</p>
                        <p className="text-sm text-gray-500">
                          {med.prescriptions} prescriptions
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">
                          ${med.revenue.toLocaleString()}
                        </p>
                        <Progress
                          value={(med.prescriptions / 156) * 100}
                          className="w-20 h-2 mt-1"
                        />
                      </div>
                    </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {!hasTopMedications && (
              <Card>
                <CardHeader>
                  <CardTitle>Top Medications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Pill className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">No medication data available</p>
                    <p className="text-sm text-gray-400">Top medications will appear here once you have analytics data</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Delivery Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {deliveryPerformance ? (
                    <>
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span>Overall Success Rate</span>
                          <span className="font-medium">{deliveryPerformance.onTimeDelivery}%</span>
                        </div>
                        <Progress value={parseFloat(deliveryPerformance.onTimeDelivery)} className="h-3" />
                      </div>
                      
                      {deliveryPerformance.hasBreakdown ? (
                        <>
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span>Same-Day Success Rate</span>
                              <span className="font-medium">{deliveryPerformance.sameDaySuccess}%</span>
                            </div>
                            <Progress value={parseFloat(deliveryPerformance.sameDaySuccess)} className="h-3" />
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span>Next-Day Success Rate</span>
                              <span className="font-medium">{deliveryPerformance.nextDaySuccess}%</span>
                            </div>
                            <Progress value={parseFloat(deliveryPerformance.nextDaySuccess)} className="h-3" />
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span>Scheduled Success Rate</span>
                              <span className="font-medium">{deliveryPerformance.scheduledSuccess}%</span>
                            </div>
                            <Progress value={parseFloat(deliveryPerformance.scheduledSuccess)} className="h-3" />
                          </div>
                        </>
                      ) : (
                        <div className="pt-2 text-xs text-gray-500">
                          * Delivery type breakdown not available - showing overall rate
                        </div>
                      )}
                      
                      <div className="pt-2 border-t">
                        <div className="flex justify-between text-sm mb-2">
                          <span>Total Delivered</span>
                          <span className="font-medium">{deliveryPerformance.totalDeliveries}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Failed Deliveries</span>
                          <span className="font-medium text-red-600">{deliveryPerformance.failedDeliveries}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500">No delivery performance data available</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Peak Hours - Calculated from hourly data */}
            {peakHours ? (
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-600 font-medium">Peak Hours</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {peakHours.peakHour}
                      </p>
                      <p className="text-sm text-blue-600">
                        {peakHours.peakOrders} orders
                      </p>
                      <div className="mt-2 text-xs text-blue-500">
                        Top 3: {peakHours.topHours.map((h, idx) => (
                          <span key={idx}>
                            {h.hour}{idx < peakHours.topHours.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <TrendingUp className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500 font-medium">Peak Hours</p>
                    <p className="text-sm text-gray-400">No hourly data available</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Best Performing Day - Calculated from delivery trends */}
            {bestPerformingDay ? (
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-600 font-medium">Best Performing Day</p>
                      <p className="text-2xl font-bold text-green-700">
                        {bestPerformingDay.day}
                      </p>
                      <p className="text-sm text-green-600">
                        {bestPerformingDay.completionRate}% completion rate
                      </p>
                    </div>
                    <Calendar className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <Calendar className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500 font-medium">Best Performing Day</p>
                    <p className="text-sm text-gray-400">No delivery trend data available</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 font-medium">
                      Avg Revenue/Day
                    </p>
                    <p className="text-2xl font-bold text-purple-700">
                      ${Math.round(pharmacyStats.totalRevenue / 30)}
                    </p>
                    <p className="text-sm text-purple-600">This month</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
